import { Form, Formik } from "formik";
import { Dialog } from "primereact/dialog";
import FormField from "components/v2/FormField";
import { FiSearch, FiX } from "react-icons/fi";
import { deleteEmptyObjects } from "utils";
import { useEffect, useState } from "react";
import { countries } from "utils/data";

const BooksFilter = ({
	visible,
	setVisible,
	publishers,
	genres,
	categories,
	filters,
	countNumberOfFiltersSet = 0,
}) => {
	const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

	useEffect(() => {
		if (countNumberOfFiltersSet > 0) {
			setShowAdvancedSearch(true);
		} else {
			setShowAdvancedSearch(false);
		}
		return () => {
			setShowAdvancedSearch(false);
		};
	}, [countNumberOfFiltersSet]);

	const initialValues = {
		searchTerm: filters.searchTerm,
		categoryIs: filters.categoryIs,
		genreIs: filters?.genreIs?.split("*") || [],
		publisherIs: filters.publisherIs,
		pricingModelIs: filters.pricingModelIs,
		page: filters.page,
		rowsPerPage: filters.rowsPerPage,
		countryIs: filters.countryIs,
		firstPage: filters.firstPage,
		publicationDateFrom: filters.publicationDateFrom,
		publicationDateTo: filters.publicationDateTo,
		priceMin: filters.priceMin,
		priceMax: filters.priceMax,
		format: filters.format,
		availability: filters.availability,
		pageCountMin: filters.pageCountMin,
		pageCountMax: filters.pageCountMax,
		language: filters.language,
	};

	const handleSubmit = (values) => {
		const params = {
			first_page: values.firstPage,
			rows: values.rowsPerPage,
			page: values.page,
			category_is: values.categoryIs,
			genre_is: values.genreIs?.join("*") || "",
			publisher_is: values.publisherIs,
			pricing_model_is: values.pricingModelIs,
			search_term: values.searchTerm,
			country_is: values.countryIs,
			publication_date_from: values.publicationDateFrom,
			publication_date_to: values.publicationDateTo,
			price_min: values.priceMin,
			price_max: values.priceMax,
			format: values.format,
			availability: values.availability,
			page_count_min: values.pageCountMin,
			page_count_max: values.pageCountMax,
			language: values.language,
			open_filters: "",
		};

		setVisible(false);

		const queryParams = deleteEmptyObjects(params);

		filters.changeAllFiltersAtOnce(queryParams);
	};

	const handleClearFilters = () => {
		const params = {
			first_page: "",
			rows: "",
			page: "",
			category_is: "",
			genre_is: "",
			publisher_is: "",
			pricing_model_is: "",
			search_term: "",
			country_is: "",
			publication_date_from: "",
			publication_date_to: "",
			price_min: "",
			price_max: "",
			format: "",
			availability: "",
			page_count_min: "",
			page_count_max: "",
			language: "",
			open_filters: "",
		};
		setVisible(false);

		const queryParams = deleteEmptyObjects(params);

		filters.changeAllFiltersAtOnce(queryParams);
	};

	const handleCloseModal = () => {
		setVisible(false);
		filters.changeOpenFilters("");
	};

	return (
		<Dialog
			showHeader={false}
			visible={visible}
			onHide={() => setVisible(false)}
			contentClassName="rounded-md font-sans relative"
			position="top"
			className="w-full"
		>
			<button
				onClick={handleCloseModal}
				className="absolute top-2 right-4 z-10 w-8 h-8 bg-akgreenest text-white rounded-full flex items-center justify-center"
			>
				<FiX size={25} />
			</button>

			<div className="mt-2 px-">
				<Formik
					initialValues={initialValues}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ setFieldValue, values }) => {
						const handleChangeGenre = (value) => {
							if (values.genreIs.includes(value)) {
								setFieldValue(
									"genreIs",
									values.genreIs.filter((genre) => genre !== value)
								);
							} else {
								setFieldValue("genreIs", [...values.genreIs, value]);
							}
						};

						return (
							<Form className="space-y-4 mt-6 pt-2">
								<div className="flex items-center justify-between gap-2">
									<div className="w-full relative">
										<FiSearch
											className="absolute left-2 top-2 z-10 text-gray-500"
											size={25}
										/>

										<FormField
											name="searchTerm"
											// label="Search book title, author, ISBN, or keyword..."
											autoFocus
											placeholder="Search book title, author, ISBN, or keyword..."
											// inputClasses="h-10"
											classNames={{
												input: "h-10 pl-10",
												label: "pl-8",
											}}
										/>

										{!showAdvancedSearch && values.searchTerm?.length > 0 && (
											<button
												type="submit"
												className="absolute right-0 top-0  px-2 py-2 rounded-r-md bg-akgreener text-white transition-all duration-200 ease-in-out hover:bg-akgreen focus:outline-none "
												style={{ width: 100, height: 40 }}
											>
												Search
											</button>
										)}
									</div>
								</div>

								<div className="flex flex-col items-start">
									<button
										type="button"
										onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
										className="bg-transparent text-blue-900 text-lg font-bold"
									>
										{showAdvancedSearch ? "Hide" : "Show"} advanced search
									</button>

									{countNumberOfFiltersSet > 0 && !showAdvancedSearch && (
										<button
											type="button"
											onClick={handleClearFilters}
											className="border-2 border-akgreener px-4 py-2 rounded-md"
										>
											Clear filters ({countNumberOfFiltersSet})
										</button>
									)}
								</div>

								<div
									className={`space-y-4 ${
										showAdvancedSearch ? "block" : "hidden"
									}  `}
								>
									<div>
										<p className="font-bold">Filter by book collection(s)</p>
										<div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
											{genres?.map((genre) => (
												<label
													key={genre?.id}
													htmlFor={`genre.${genre?.id}`}
													className={`flex gap-2 items-center px-3 py-2 rounded-md  cursor-pointer transition-all duration-200 ease-in-out bg-gray-200 border-2 ${
														values?.genreIs?.includes(genre?.name)
															? "border-akgreen "
															: "border-transparent hover:border-akgreen  "
													}`}
												>
													<input
														type="checkbox"
														checked={values?.genreIs?.includes(genre?.name)}
														id={`genre.${genre?.id}`}
														name="genreIs"
														value={genre?.name}
														onChange={(e) => handleChangeGenre(e.target.value)}
														className="h-4 w-4 rounded-md border-2 border-transparent focus:ring-2 focus:ring-akgreener focus:ring-offset-2 focus:ring-offset-gray-200 transition-all duration-200 ease-in-out"
													/>
													<span
														title={genre?.name}
														className="truncate w-40"
													>
														{genre?.name}
													</span>
												</label>
											)) || []}
										</div>
									</div>

									<select
										name="publisherIs"
										value={values?.publisherIs}
										onChange={(e) =>
											setFieldValue("publisherIs", e.target.value)
										}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option label="- Filter by publisher -"></option>
										{publishers?.map((publisher) => (
											<option
												key={publisher?.id}
												value={publisher.id?.toString()}
											>
												{publisher.name}
											</option>
										))}
									</select>

									<select
										name="categoryIs"
										value={values?.categoryIs}
										onChange={(e) =>
											setFieldValue("categoryIs", e.target.value)
										}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option label="- Filter by category -"></option>
										{categories?.map((category) => (
											<option
												key={category?.name}
												value={category.name}
											>
												{category.name}
											</option>
										))}
									</select>

									<select
										name="pricingModelIs"
										value={values?.pricingModelIs}
										onChange={(e) =>
											setFieldValue("pricingModelIs", e.target.value)
										}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option label="- Filter by pricing model -"></option>
										<option value="sellable">Purchase only</option>
										<option value="subscription">Subscription only</option>
										<option value="free">Free books</option>
										<option value="all">Purchase and subscription</option>
									</select>

									<select
										name="countryIs"
										value={values?.countryIs}
										onChange={(e) => setFieldValue("countryIs", e.target.value)}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option label="- Filter by country -"></option>
										{countries?.map((country, index) => (
											<option
												key={index}
												value={country?.name}
											>
												{country?.name}
											</option>
										))}
									</select>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Publication date from</label>
											<input
												type="date"
												value={values?.publicationDateFrom || ""}
												onChange={(e) => setFieldValue("publicationDateFrom", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Publication date to</label>
											<input
												type="date"
												value={values?.publicationDateTo || ""}
												onChange={(e) => setFieldValue("publicationDateTo", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Price min (USD)</label>
											<input
												type="number"
												min="0"
												step="0.01"
												placeholder="0"
												value={values?.priceMin || ""}
												onChange={(e) => setFieldValue("priceMin", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Price max (USD)</label>
											<input
												type="number"
												min="0"
												step="0.01"
												placeholder="Any"
												value={values?.priceMax || ""}
												onChange={(e) => setFieldValue("priceMax", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
									</div>

									<select
										name="format"
										value={values?.format}
										onChange={(e) => setFieldValue("format", e.target.value)}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option value="">- Filter by format -</option>
										<option value="digital">Digital</option>
										<option value="physical">Physical</option>
										<option value="both">Both</option>
									</select>

									<select
										name="availability"
										value={values?.availability}
										onChange={(e) => setFieldValue("availability", e.target.value)}
										className="w-full h-10 rounded-lg px-2 bg-gray-100"
									>
										<option value="">- Filter by availability -</option>
										<option value="in_stock">In stock</option>
										<option value="all">All</option>
									</select>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Page count min</label>
											<input
												type="number"
												min="0"
												placeholder="0"
												value={values?.pageCountMin || ""}
												onChange={(e) => setFieldValue("pageCountMin", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Page count max</label>
											<input
												type="number"
												min="0"
												placeholder="Any"
												value={values?.pageCountMax || ""}
												onChange={(e) => setFieldValue("pageCountMax", e.target.value)}
												className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
											/>
										</div>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Language (e.g. en, fr)</label>
										<input
											type="text"
											placeholder="e.g. en"
											value={values?.language || ""}
											onChange={(e) => setFieldValue("language", e.target.value)}
											className="w-full h-10 rounded-lg px-2 bg-gray-100 border border-gray-200"
										/>
									</div>

									<div className="flex justify-between item-center">
										<button
											type="button"
											onClick={handleCloseModal}
											className="border-2 border-transparent bg-gray-200 text-akgreener px-4 py-2 rounded-md"
										>
											Close
										</button>

										<div className="flex items-center justify-end item-center gap-2">
											{countNumberOfFiltersSet > 0 && (
												<button
													type="button"
													onClick={handleClearFilters}
													className="border-2 border-akgreener px-4 py-2 rounded-md"
												>
													Clear filters ({countNumberOfFiltersSet})
												</button>
											)}

											<button
												type="submit"
												className="border-2 border-transparent bg-akgreener text-white px-4 py-2 rounded-md"
												style={{ width: 100 }}
											>
												Search
											</button>
										</div>
									</div>
								</div>
							</Form>
						);
					}}
				</Formik>
			</div>
		</Dialog>
	);
};

export default BooksFilter;
